import * as React from 'react'
import {
  conversationElapsedMs,
  conversationToolCallEntries
} from '../../../components/Chat/conversation-tool-call-entries'
import { usePersistedConversation } from '../../../components/Chat/usePersistedConversation'
import { SidebarToolCallList } from '../../../components/ToolTrace/ToolCallRow'
import { Card } from '../../../components/ui/card'
import { formatDurationMs } from '../../../lib/format-duration'
import { cn } from '../../../lib/utils'
import { CollapsibleCard } from './CollapsibleCard'
import { SECTION_LABEL } from './compact'

const LIVE_REFRESH_MS = 5000

/**
 * A job agent's tool calls, read from the job's persisted conversation and
 * drawn with the shared tool log. The job row only changes at phase
 * boundaries, not per tool call, and conversation turns publish no wake-up,
 * so while `live` the conversation reloads on a deliberate clock; once
 * settled it reloads when `settledKey` changes.
 */
export function ConversationToolCallsCard({
  conversationId,
  live,
  settledKey,
  emptyText,
  loadErrorText,
  className,
  listClassName,
  collapsible = false,
  testId
}: {
  conversationId: string
  live: boolean
  settledKey?: string
  emptyText: string
  loadErrorText: string
  className?: string
  /** Wraps the list, e.g. to cap its height on a page with other cards. */
  listClassName?: string
  /** The header folds the list away; it starts open. */
  collapsible?: boolean
  testId?: string
}) {
  const [tick, setTick] = React.useState(0)
  React.useEffect(() => {
    if (!live) return
    const timer = setInterval(() => setTick((value) => value + 1), LIVE_REFRESH_MS)
    return () => clearInterval(timer)
  }, [live])
  const { turns, loadError } = usePersistedConversation(
    conversationId,
    live ? tick : settledKey,
    loadErrorText
  )

  if (loadError) {
    return (
      <p role="alert" className="text-xs text-st-down">
        {loadError}
      </p>
    )
  }
  const calls = turns ? conversationToolCallEntries(turns) : []
  const elapsedMs = turns ? conversationElapsedMs(turns) : null
  const summary = turns
    ? `${calls.length} tool call${calls.length === 1 ? '' : 's'}${elapsedMs === null ? '' : ` · ${formatDurationMs(elapsedMs)}`}`
    : undefined
  const body = !turns ? (
    <p className="text-xs text-ink-3">Loading tool calls…</p>
  ) : calls.length === 0 ? (
    <p className="text-xs text-ink-3">{emptyText}</p>
  ) : (
    <div className={cn('min-w-0', listClassName)}>
      <SidebarToolCallList calls={calls} />
    </div>
  )
  if (collapsible) {
    return (
      <CollapsibleCard
        title="Tool calls"
        meta={summary}
        defaultOpen
        {...(className ? { className } : {})}
        {...(testId ? { testId } : {})}
      >
        {body}
      </CollapsibleCard>
    )
  }
  return (
    <Card className={cn('gap-3 p-4', className)} data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={SECTION_LABEL}>Tool calls</h2>
        {summary ? <span className="text-11 text-ink-3">{summary}</span> : null}
      </div>
      {body}
    </Card>
  )
}
